// src/services/deployService.js

import useSelectionStore from "@store/useSelectionStore";
import useGameStore from "@store/useGameStore";
import useMapStore from "@store/useMapStore";
import { deployUnitApi } from "@api/gameApi";
import { ClickContext, ClickableType } from "@utils/enums";
import { ClickDTO } from "@models/ClickDTO";
import feedback from "@components/ui/general/feedback/feedback";
import calculateDeployable from "@helpers/game/calculateDeployable";

// Global flag to prevent race conditions when deploying rapidly
let isDeploying = false;

/**
 * Enter deployment mode for a specific unit
 * Calculates deployable tiles and updates map visuals
 */
export function enterDeployMode(unit) {
  const { game } = useGameStore.getState();
  const { setHighlightTiles, setInteractionMode, setOverlayMode } = useMapStore.getState();
  const { setClickContext } = useSelectionStore.getState();

  if (!unit || !game) {
    console.warn("[deployService] Cannot enter deploy mode: missing unit or game");
    return;
  }

  // console.log("[deployService] Entering deploy mode for unit:", unit.name);

  // Calculate deployable tiles
  const tiles = calculateDeployable(game, unit);

  // console.log("[deployService] Deployable tiles calculated:", tiles.length);

  // Update map visuals
  setHighlightTiles(tiles);
  setOverlayMode("deploy");
  setInteractionMode("deploy");

  // Update selection context
  setClickContext(ClickContext.DEPLOY_UNIT);
}

/**
 * Exit deployment mode and clear all visuals
 */
export function exitDeployMode() {
  const { clearVisuals, setInteractionMode } = useMapStore.getState();
  const { setClickContext } = useSelectionStore.getState();

  // console.log("[deployService] Exiting deploy mode");

  clearVisuals();
  setInteractionMode("select");
  setClickContext(ClickContext.SELECT);
}

/**
 * Deploy unit at target position
 * Validates position, calls API, and auto-selects next unit
 */
export async function deployAt(x, y) {
  const { currentClick } = useSelectionStore.getState();
  const { game } = useGameStore.getState();
  const { highlightTiles } = useMapStore.getState().visual;
  const { addProcessingTile, removeProcessingTile } = useMapStore.getState();

  // Get the deployable unit from currentClick
  const unit = currentClick?.data;

  if (!unit) {
    throw new Error('[deployService] No unit selected for deployment');
  }

  if (!game) {
    throw new Error('[deployService] No game data available');
  }

  // Validate tile is in highlight
  const canDeploy = highlightTiles.some((t) => t.x === x && t.y === y);
  if (!canDeploy) {
    feedback.error("Cannot deploy at this position");
    return;
  }

  // Block duplicate clicks while processing
  const { isProcessingTile } = useMapStore.getState();
  if (isProcessingTile(x, y)) {
    return;
  }

  // Block if another deploy is in progress (prevents race condition)
  if (isDeploying) {
    console.warn("[deployService] Deploy already in progress, ignoring");
    return;
  }

  isDeploying = true;

  // Show processing indicator while API call is pending
  addProcessingTile(x, y, 'deploy');

  try {
    // console.log(`[deployService] Deploying ${unit.name} at (${x}, ${y})`);
    await deployUnitApi(game.id, unit.id, x, y);
    feedback.success(`${unit.name} deployed!`);
    selectNextDeployableUnit(unit.id);
  } catch (err) {
    // Error logging and feedback handled centrally by api.js
    // Only show generic message if api.js didn't show one
    if (!err.userMessage) {
      feedback.error("Failed to deploy unit");
    }
  } finally {
    // Always remove processing indicator and reset deploying flag
    removeProcessingTile(x, y);
    isDeploying = false;
  }
}

/**
 * After deploying a unit, auto-select the next deployable unit
 * If no more deployable units, select the first deployed unit
 */
function selectNextDeployableUnit(justDeployedUnitId) {
  const { clearVisuals, setInteractionMode } = useMapStore.getState();
  const { setClickContext } = useSelectionStore.getState();
  const { getDeployableUnits, getCurrentUnits } = useGameStore.getState();

  // Clear current deploy mode visuals first
  clearVisuals();
  setInteractionMode("select");
  setClickContext(ClickContext.SELECT);

  // Get deployable units (excluding the one just deployed)
  const deployableUnits = getDeployableUnits();
  const deployedUnits = getCurrentUnits();
  const deployedIds = new Set(deployedUnits.map(u => u.id));

  // Add the just-deployed unit to deployed set (API response may not be processed yet)
  deployedIds.add(justDeployedUnitId);

  const availableUnits = deployableUnits.filter(u => !deployedIds.has(u.id));

  if (availableUnits.length > 0) {
    // Select the first available deployable unit
    const nextUnit = availableUnits[0];
    enterDeployMode(nextUnit);

    // Update currentClick to show unit info
    const clickDTO = new ClickDTO(ClickableType.UNIT, nextUnit, 'deployService');
    useSelectionStore.setState({ currentClick: clickDTO });
  } else if (deployedUnits.length > 0) {
    // No more deployable units - select first deployed unit
    const firstDeployed = deployedUnits[0];
    const clickDTO = new ClickDTO(ClickableType.UNIT, firstDeployed, 'deployService');
    useSelectionStore.setState({ currentClick: clickDTO });
  }
}

// Export as object for consistency
const deployService = {
  enterDeployMode,
  exitDeployMode,
  deployAt
};

export default deployService;
